import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTVRemoteControl, useScrollManagement } from '../hooks'
import {
  getRequestAssets,
  postRequestSignedUrls
} from '../../../assets/requests'
import AssetList from './AssetList'
import LoadMoreButton from './LoadMoreButton'
import placeholderImg from '../../../assets/images/camera.jpg'
import '../../../assets/stylesheets/assets.css'
import { useUserInfo } from '../../Authentication/hooks'
import { useAuthManagement } from '../../Header/hooks'

const InfiniteScrollContainer = ({
  dispatch,
  state,
  isLoading,
  setIsLoading,
  type = 'image'
}) => {
  const navigate = useNavigate()
  const [loadedImages, setLoadedImages] = useState({})
  const [hasMoreData, setHasMoreData] = useState(false)
  const { userId } = useUserInfo()
  const { isAuthComplete } = useAuthManagement()

  // Refs for managing state and scroll
  const currentPage = useRef(1)
  const sectionRef = useRef(null)
  const loadMoreButtonRef = useRef(null)
  const isLoadingRef = useRef(false)
  const navigationTimeoutRef = useRef(null)

  const selectedCategoriesString = useMemo(
    () => JSON.stringify(state.selectedCategories),
    [state.selectedCategories]
  )

  const stableSelectedCategories = useMemo(
    () => state.selectedCategories,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCategoriesString]
  )

  const stableDispatch = useCallback(dispatch, [dispatch])

  const config = useMemo(
    () => ({
      userId: userId,
      pageLimit: 20,
      categories: stableSelectedCategories,
      type: type,
      dispatch: stableDispatch
    }),
    [userId, stableSelectedCategories, type, stableDispatch]
  )

  function delta(source, destination) {
    let sourceIds = Object.keys(source)
    let destinationIds = Object.keys(destination)
    let differenceIds = sourceIds.filter(x => !destinationIds.includes(x))
    let difference = {}
    differenceIds.forEach(
      differenceId => (difference[differenceId] = source[differenceId])
    )
    return difference
  }

  const sign = async (userId, collection) => {
    if (!userId || !isAuthComplete) {
      console.warn('Cannot sign assets: authentication not complete')
      return collection
    }

    const token = localStorage.getItem('token')
    if (!token) {
      console.error('Cannot sign assets: no authentication token available')
      return collection
    }

    let collectionIds = Object.keys(collection)
    if (collectionIds.length === 0) return collection

    let thumbnailKeys = []
    let s3Uris = []
    let s3UriIdx = []

    collectionIds.forEach((id, idx) => {
      const asset = collection[id]

      if (asset.thumbnails && asset.thumbnails.length > 0) {
        thumbnailKeys.push(asset.thumbnails[1])
      }

      // add s3uri for signing if we haven't signed it yet
      if (!asset.fileUrl) {
        s3Uris.push(asset.s3uri)
        s3UriIdx.push(idx)
      }
    })

    try {
      const [signedThumbnailUrls, signedS3Uris] = await Promise.all([
        thumbnailKeys.length > 0
          ? postRequestSignedUrls({ userId, payload: thumbnailKeys })
          : [],
        s3Uris.length > 0
          ? postRequestSignedUrls({ userId, payload: s3Uris })
          : []
      ])

      collectionIds.forEach((id, idx) => {
        // set thumbnailUrl if we signed the thumbnail S3 URI
        if (signedThumbnailUrls && signedThumbnailUrls[idx]) {
          collection[id].thumbnailUrl = signedThumbnailUrls[idx]
        } else {
          collection[id].thumbnailUrl = placeholderImg
        }

        // set fileUrl if we signed s3uri
        const s3UriSignedIndex = s3UriIdx.indexOf(idx)
        if (
          s3UriSignedIndex !== -1 &&
          signedS3Uris &&
          signedS3Uris[s3UriSignedIndex]
        ) {
          collection[id].fileUrl = signedS3Uris[s3UriSignedIndex]
        }
      })
    } catch (error) {
      console.error('Error signing assets:', error)
      // Set placeholder images for all assets if signing fails
      collectionIds.forEach(id => {
        collection[id].thumbnailUrl = placeholderImg
      })
    }

    return collection
  }

  const mapToKinstakAssets = useCallback(
    async fromMongo => {
      if (!Array.isArray(fromMongo)) {
        console.error('Expected an array, but received:', fromMongo)
        return {}
      }

      var assets = {}

      await fromMongo.forEach((key, index) => {
        const kinstakAsset = {
          id: '',
          name: '',
          metadata: {},
          categoryIds: [],
          thumbnails: [],
          src: '',
          key: '',
          createdAt: '',
          s3uri: '',
          type: type
        }

        kinstakAsset.id = key.id
        kinstakAsset.name = ''
        kinstakAsset.thumbnailUrl = ''
        kinstakAsset.s3uri = key.s3uri
        kinstakAsset.createdAt = key.createdAt
        kinstakAsset.thumbnails = key.thumbnails
        kinstakAsset.metadata['categories'] = key.categories
        kinstakAsset.metadata['content'] = key.content
        kinstakAsset.key = key['s3uri'].split('/').slice(-1)[0]
        assets[key.id] = kinstakAsset
      })

      return assets
    },
    [type]
  )

  const QueryAssets = useCallback(
    async config => {
      if (!userId || !isAuthComplete) {
        console.warn('Cannot query assets: authentication not complete')
        return []
      }

      const token = localStorage.getItem('token')
      if (!token) {
        console.error('Cannot query assets: no authentication token available')
        return []
      }

      try {
        const tempConfig = {
          ...config,
          currentPage: { current: currentPage.current }
        }

        let mongoAssets = await getRequestAssets(tempConfig)

        // Determine if there are more assets to fetch based on response size
        const pageLimit = tempConfig.pageLimit || 20
        if (mongoAssets?.length === 0) {
          setHasMoreData(false)
        } else if (mongoAssets?.length < pageLimit) {
          setHasMoreData(false)
        } else {
          setHasMoreData(true)
        }

        currentPage.current += 1

        const mappedAssets = await mapToKinstakAssets(mongoAssets)

        config.dispatch({ type: 'append_raw_assets', payload: mappedAssets })
      } catch (error) {
        console.error('Error querying assets:', error)
        setHasMoreData(false)
      }
    },
    [userId, isAuthComplete, setHasMoreData, mapToKinstakAssets]
  )

  const getAssets = useCallback(
    async config => {
      if (userId && isAuthComplete) {
        QueryAssets(config)
      }
    },
    [QueryAssets, userId, isAuthComplete]
  )

  const fetchData = useCallback(async () => {
    if (
      isLoadingRef.current ||
      (!hasMoreData && currentPage.current > 1) ||
      !userId ||
      !isAuthComplete
    ) {
      return
    }

    isLoadingRef.current = true
    setIsLoading(true)
    assetsLoaded.current = false

    try {
      await getAssets(config)
    } catch (err) {
      console.error('Error in fetchData:', err)
    } finally {
      isLoadingRef.current = false
      setIsLoading(false)
      assetsLoaded.current = true
    }
  }, [hasMoreData, config, getAssets, setIsLoading, userId, isAuthComplete])

  const handleLoadMore = useCallback(
    e => {
      if (e?.target) {
        e.target.classList.remove('active')
        e.target.classList.add('disabled')
        e.target.innerHTML = `<div class="lds-roller"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>`
      }

      fetchData()
    },
    [fetchData]
  )

  const clickLoadMoreButton = useCallback(() => {
    if (loadMoreButtonRef.current && !isLoadingRef.current && hasMoreData) {
      handleLoadMore()
    }
  }, [hasMoreData, handleLoadMore])

  const { scrollUp, scrollDown, scrollToTop } = useScrollManagement({
    containerRef: sectionRef,
    isLoading,
    modalActive: state.modalActive,
    onLoadMore: (...args) => {
      clickLoadMoreButton()
    },
    enableInfiniteScroll: true,
    enableContainerFocus: true,
    scrollAmount: 200,
    backToTopAmount: 5000,
    throttleDelay: 500,
    infiniteScrollOffset: 5
  })

  // TV Remote Control hook for asset scrolling
  const { focusElement } = useTVRemoteControl({
    onScrollUp: scrollUp,
    onScrollDown: scrollDown,
    onLoadMore: clickLoadMoreButton,
    enableGalleryNavigation: false,
    enableModalNavigation: false,
    enableAssetScrolling: true,
    loadTVStyles: false
  })

  // Refs

  // Used to ensure that effects that depend on the effect that loads assets know when it is finished.
  // In particular, this prevents unwanted navigation to the upload page,
  // as the isLoading boolean can't be relied on to change between effect executions;
  // it only changes on a complete re-render of the component.
  const assetsLoaded = useRef(false)

  const rawAssetsKeys = useMemo(
    () => JSON.stringify(Object.keys(state.rawAssets).sort()),
    [state.rawAssets]
  )

  const signedAssetsKeys = useMemo(
    () => JSON.stringify(Object.keys(state.signedAssets).sort()),
    [state.signedAssets]
  )

  const signedAssetsCount = useMemo(
    () => Object.keys(state.signedAssets).length,
    [state.signedAssets]
  )

  const publishedAssetsCount = useMemo(
    () => Object.keys(state.publishedAssets).length,
    [state.publishedAssets]
  )

  // Only show asset list when we are explicitly in an asset-view context
  const isAssetView = useMemo(() => {
    if (!state.showAssets) return false
    const sc = state.selectedCategories || []
    if (sc.length === 2) return true
    if (sc.length === 1 && sc[0]?.type === 'year') return true
    return false
  }, [state.showAssets, state.selectedCategories])

  // Redirect to upload ONLY after an initial fetch attempt has completed
  // and we still have no assets and no navigation (brand new account)
  useEffect(() => {
    const noPublished = Object.keys(state.publishedAssets || {}).length === 0
    const noRaw = Object.keys(state.rawAssets || {}).length === 0
    const noSigned = Object.keys(state.signedAssets || {}).length === 0
    const atRoot = (state.selectedCategories || []).length === 0
    const noNavigation = Array.isArray(state.navigation)
      ? state.navigation.length === 0
      : true

    if (
      userId &&
      isAuthComplete &&
      atRoot &&
      noPublished &&
      noRaw &&
      noSigned &&
      noNavigation &&
      currentPage.current > 1 // ensures we attempted to fetch at least once
    ) {
      navigate('/upload', { state: { redirectedFromGallery: true } })
    }
  }, [
    state.publishedAssets,
    state.selectedCategories,
    state.rawAssets,
    state.signedAssets,
    state.navigation,
    navigate,
    userId,
    isAuthComplete
  ])

  // Sign assets when rawAssets change and wait for auth
  useEffect(() => {
    if (
      userId &&
      isAuthComplete &&
      Object.keys(state.rawAssets).length >
        Object.keys(state.signedAssets).length
    ) {
      const deltaAssets = delta(state.rawAssets, state.signedAssets)

      if (Object.keys(deltaAssets).length > 0) {
        sign(userId, deltaAssets).then(deltas => {
          stableDispatch({ type: 'append_signed_assets', payload: deltas })
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawAssetsKeys, signedAssetsKeys, userId, isAuthComplete, stableDispatch])

  // Publish signed assets
  useEffect(() => {
    if (signedAssetsCount > publishedAssetsCount) {
      const deltaAssets = delta(state.signedAssets, state.publishedAssets)

      if (Object.keys(deltaAssets).length > 0) {
        stableDispatch({
          type: 'append_published_assets',
          payload: deltaAssets
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedAssetsCount, publishedAssetsCount, stableDispatch])

  // Reset and fetch when categories change
  useEffect(() => {
    if (!userId || !isAuthComplete) return

    stableDispatch({ type: 'set_raw_assets', payload: {} })
    stableDispatch({ type: 'set_signed_assets', payload: {} })
    stableDispatch({ type: 'set_published_assets', payload: {} })

    currentPage.current = 1
    setHasMoreData(false)

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoriesString, stableDispatch, userId, isAuthComplete])

  // Handle loading state for load more button
  useEffect(() => {
    if (isLoading) {
      let target = document.getElementsByClassName('load-more-btn')[0]
      if (target) {
        console.log('Load more button found:', target)
      }
    }
  }, [isLoading])

  // Focus section on mount using TV remote hook
  useEffect(() => {
    focusElement(sectionRef.current)
  }, [focusElement])

  useEffect(() => {
    const timeoutId = navigationTimeoutRef.current
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  return (
    <div className="assets">
      <div className="assets-container">
        {isAssetView && publishedAssetsCount > 0 && (
          <AssetList
            state={state}
            dispatch={dispatch}
            sectionRef={sectionRef}
            onScrollUp={scrollUp}
            onScrollDown={scrollDown}
            onBackToTop={scrollToTop}
            onLoadMore={handleLoadMore}
            loadedImages={loadedImages}
            setLoadedImages={setLoadedImages}
          />
        )}
      </div>
      <div className="load-more-container">
        {isAssetView && publishedAssetsCount > 0 && hasMoreData && (
          <LoadMoreButton
            ref={loadMoreButtonRef}
            onLoadMore={handleLoadMore}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  )
}

export default InfiniteScrollContainer
